/**
 * `41` — *set {status, columnId} to {something, statusColumnValue}*.
 *
 * Writing the index the cell already holds is a no-op: `cells.setCellValue` runs the column
 * type's `isEqual` (R4) and emits nothing, which is the main cycle breaker for the many
 * "status A → status B" pairs on this account.
 */
import { z } from 'zod';
import { defineAction } from '../block.js';
import { assertLabelExists } from '../lint.js';
import { columnRefSchema, isAnyValue, labelValueSchema } from '../values.js';
export const setStatusParams = z.object({
    columnId: columnRefSchema,
    statusColumnValue: labelValueSchema,
});
export const setStatusAction = defineAction({
    id: '41',
    kind: 'action',
    app: 'monday',
    sentencePartial: 'set {status, columnId} to {something, statusColumnValue}',
    paramsSchema: setStatusParams,
    requiredColumnTypes: ['status'],
    executable: true,
    references: (params) => [{ kind: 'column', id: params.columnId.columnId, invalid: params.columnId.invalid }],
    async execute(context, params) {
        if (context.itemId === null)
            return { skipped: 'no_item' };
        if (isAnyValue(params.statusColumnValue))
            throw new Error('block 41: "anything" is not a writable status value');
        const item = await context.services.getItem(context.itemId);
        if (item === null)
            return { skipped: 'item_deleted' };
        /**
         * Round 9l: monday lets a label be deleted out from under an automation that sets it — board
         * *Activities* still carries an `active` row pointing at label `1` of a column whose labels
         * are `{3, 4}` — and monday never marks the row. Asking the column first turns the resulting
         * raw `ColumnValueError` into a reference failure the run history can explain and the engine
         * must not count as a failure (it would park the automation after three ticks). A column the
         * port cannot answer for is left to the service layer, exactly as before.
         */
        const column = await context.services.getColumn(context.boardId, params.columnId.columnId);
        if (column !== null) {
            assertLabelExists({ id: column.id, type: column.type, settings: column.settings }, params.statusColumnValue.index, '41');
        }
        const result = await context.services.setCellValue({
            boardId: context.boardId,
            itemId: context.itemId,
            columnId: params.columnId.columnId,
            value: { index: params.statusColumnValue.index },
        });
        return { itemId: context.itemId, columnId: params.columnId.columnId, index: params.statusColumnValue.index, changed: result.changed };
    },
});
//# sourceMappingURL=41.js.map